import { JSX, useCallback, useMemo } from "react";

import Grid from "@mui/material/Grid";
import InputAdornment from "@mui/material/InputAdornment";
import Typography from "@mui/material/Typography";

import ManagedTextField from "utils/edit/ManagedTextField";
import { ChangeEvent } from "utils/history/Event";
import { isEmpty } from "utils/Utils";

import {
	InformationRight,
	InformationRights
} from "core/blueprint/trajectory/TrajectoryBlueprintService";
import { CalculationField } from "../FieldService";
import ExpressionParser from "../parser/ExpressionParser";

export type CalculationInputProps = {
	field: CalculationField;
	editable: boolean;
	rights: InformationRights;
	parser?: ExpressionParser;

	onChange: (event: ChangeEvent, valid: boolean) => void;
};

export default function CalculationInput(
	props: Readonly<CalculationInputProps>
): JSX.Element {
	const evaluatedValue = useMemo(
		() => props.parser?.parse(props.field.value ?? props.field.value),
		[props.field.value, props.parser]
	);

	const isValid = useCallback(
		(value: string): boolean =>
			props.field.required ? !isEmpty(value) : true,
		[props.field.required]
	);

	const onChange = useCallback(
		(newValue: string): void => {
			const change = new ChangeEvent(
				"/value",
				props.field.value,
				newValue
			);
			props.onChange(change, isValid(newValue));
		},
		[props.onChange, isValid, props.field.value]
	);

	return (
		<Grid
			item
			container
			xs={12}
			md={6}
			columnSpacing={2}
			alignItems="center">
			<Grid item xs={12} sm={6}>
				<Typography variant="subtitle2" sx={{ wordWrap: "break-word" }}>
					{props.field.name}
					{props.field.required && " *"}
				</Typography>
			</Grid>
			<Grid item xs={12} sm={6}>
				{props.editable &&
				props.rights[props.field.uuid][InformationRight.WRITE] ? (
					<ManagedTextField
						id={props.field.name}
						variant="standard"
						fullWidth
						required={props.field.required}
						error={!isValid(props.field.value)}
						value={props.field.value}
						InputProps={{
							startAdornment: (
								<InputAdornment position="start">
									=
								</InputAdornment>
							)
						}}
						onUpdate={onChange}
					/>
				) : (
					<Typography variant="body2" sx={{ wordWrap: "break-word" }}>
						{evaluatedValue}
					</Typography>
				)}
			</Grid>
		</Grid>
	);
}
